import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Video } from 'lucide-react';
import { useTour } from '@/contexts/TourContext';
import { LEAD_DETAIL_TOUR } from '@/lib/tourDefinitions';

import { LeadInfoCard } from '@/components/lead-detail/LeadInfoCard';
import { LeadScoreCard } from '@/components/lead-detail/LeadScoreCard';
import { LeadNotesSection } from '@/components/lead-detail/LeadNotesSection';
import { LeadDocumentsSection } from '@/components/lead-detail/LeadDocumentsSection';
import { LeadActivityLog } from '@/components/lead-detail/LeadActivityLog';
import { LeadEditDialog } from '@/components/lead-detail/LeadEditDialog';
import { LeadDetailShareDialog } from '@/components/lead-detail/LeadShareDialog';
import EmailComposeDialog from '@/components/EmailComposeDialog';
import EmailHistory from '@/components/EmailHistory';
import { LeadSmsPanel } from '@/components/lead-detail/LeadSmsPanel';
import NearbyExperts from '@/components/lead-detail/NearbyExperts';
import logger from '@/lib/logger';
import { enqueue } from '@/lib/offlineQueue';
import { safePrint } from '@/lib/safePrint';

export default function LeadDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [shareTab, setShareTab] = useState('members');
  const [shareNote, setShareNote] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const { startTour, loaded: tourLoaded, isTourCompleted } = useTour();

  const fetchLead = useCallback(() => {
    api.getLead(id).then(r => setLead(r.data)).catch(() => { toast.error('Lead not found'); navigate('/leads'); }).finally(() => setLoading(false));
  }, [id, navigate]);

  const fetchDocuments = useCallback(() => {
    api.getDocuments(id).then(r => setDocuments(r.data)).catch(err => logger.error(err));
  }, [id]);

  useEffect(() => { fetchLead(); fetchDocuments(); }, [fetchLead, fetchDocuments]);

  // Refetch when offline queue flushes (e.g. queued notes sent)
  useEffect(() => {
    const onFlushed = () => { fetchLead(); };
    window.addEventListener('offline-queue:flushed', onFlushed);
    return () => window.removeEventListener('offline-queue:flushed', onFlushed);
  }, [fetchLead]);

  useEffect(() => {
    if (tourLoaded && lead && !loading && !isTourCompleted('lead-detail')) {
      const timer = setTimeout(() => startTour('lead-detail', LEAD_DETAIL_TOUR.steps), 1200);
      return () => clearTimeout(timer);
    }
  }, [tourLoaded, lead, loading, isTourCompleted, startTour]);

  const handleAddNote = async () => {
    const text = note.trim();
    if (!text) return;
    setSubmitting(true);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      try {
        await enqueue({ kind: 'lead-note', leadId: id, payload: { content: text } });
        toast.success("Note queued — we'll sync it when you're back online");
        setNote('');
      } catch (e) {
        toast.error('Could not queue note — try again');
        logger.error('offline enqueue failed', e);
      }
      setSubmitting(false);
      return;
    }
    try { await api.addNote(id, text); toast.success('Note added'); setNote(''); fetchLead(); }
    catch (e) {
      // Network error while "online" flag was stale → fall back to queue
      if (!e.response) {
        try {
          await enqueue({ kind: 'lead-note', leadId: id, payload: { content: text } });
          toast.success("Note queued — we'll sync it when you're back online");
          setNote('');
        } catch (q) {
          toast.error('Could not queue note — try again');
          logger.error('offline enqueue fallback failed', q);
        }
      } else {
        toast.error(e.response?.data?.detail || 'Failed to add note');
      }
    }
    setSubmitting(false);
  };

  const handleUploadDocument = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File must be under 25MB'); return; }
    setUploading(true);
    try { await api.uploadDocument(id, file); toast.success(`"${file.name}" uploaded`); fetchDocuments(); fetchLead(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await api.downloadDocument(id, doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = doc.filename; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { logger.error(err); toast.error('Download failed'); }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try { await api.deleteDocument(id, docId); toast.success('Document deleted'); fetchDocuments(); fetchLead(); }
    catch { toast.error('Failed to delete'); }
  };

  const openShare = async () => {
    setSelectedMembers([]); setSelectedGroups([]); setShareTab('members'); setShowShare(true);
    try { const r = await api.getMembers(); setMembers(r.data.filter(m => m.id !== user?.id && m.id !== user?._id)); } catch (err) { logger.error(err); }
    try { const r = await api.getGroups(); setGroups(r.data); } catch (err) { logger.error(err); }
  };

  const handleShare = async () => {
    if (selectedMembers.length === 0 && selectedGroups.length === 0) return;
    try {
      if (selectedMembers.length > 0) await api.shareLead(id, selectedMembers, shareNote);
      for (const gid of selectedGroups) await api.shareLeadsToGroup(gid, [id]);
      toast.success('Lead shared'); setShowShare(false); setShareNote(''); fetchLead();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to share'); }
  };

  const handleSnap = async () => {
    try {
      if (lead.snapped_by) {
        await api.unsnapLead(id);
        toast.success('Lead unsnapped — now visible to shared members');
      } else {
        await api.snapLead(id);
        toast.success('Lead snapped — now hidden from other members');
      }
      fetchLead();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openEditLead = () => {
    setEditForm({
      name: lead.name || '', email: lead.email || '', phone: lead.phone || '',
      company: lead.company || '', address: lead.address || '', city: lead.city || '',
      state: lead.state || '', zip_code: lead.zip_code || '', status: lead.status || 'new',
      category: lead.category || '', lead_source: lead.lead_source || '',
      tags: lead.tags?.join(', ') || '',
      number_of_employees: lead.number_of_employees ?? null,
      annual_revenue: lead.annual_revenue ?? null,
      website: lead.website || '',
    });
    setShowEdit(true);
  };

  const handleEditLead = async () => {
    if (!editForm.name?.trim()) { toast.error('Name is required'); return; }
    try {
      const payload = { ...editForm };
      if (payload.tags) payload.tags = payload.tags.split(',').map(t => t.trim()).filter(Boolean);
      else payload.tags = [];
      await api.updateLead(id, payload);
      toast.success('Lead updated'); setShowEdit(false); fetchLead();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handlePrintLead = () => {
    const notes = lead.notes || [];
    const activities = lead.activities || [];
    const html = `<html><head><title>${lead.name} - Real Leads CRM</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}body{font-family:'IBM Plex Sans',Arial,sans-serif;padding:32px;color:#0F172A}
      h1{font-size:22px;font-weight:700;margin-bottom:4px}h2{font-size:14px;font-weight:600;margin:20px 0 8px;padding-bottom:6px;border-bottom:1px solid #E2E8F0}
      .status{display:inline-block;padding:2px 8px;border-radius:2px;font-size:11px;font-weight:600;background:#EFF6FF;color:#0047AB}
      .field{margin-bottom:8px}.field-label{font-size:10px;text-transform:uppercase;letter-spacing:.12em;color:#64748B;margin-bottom:2px}.field-value{font-size:13px;font-weight:500}
      .tag{display:inline-block;padding:1px 6px;background:#F1F5F9;border-radius:2px;font-size:11px;margin-right:4px;margin-bottom:4px}
      .note{border:1px solid #E2E8F0;border-radius:2px;padding:10px 12px;margin-bottom:6px}.note-meta{font-size:10px;color:#64748B;margin-bottom:4px}.note-text{font-size:12px}
      .activity{padding:6px 0;font-size:12px;border-bottom:1px solid #F1F5F9}.activity-time{font-size:10px;color:#94A3B8}
      .footer{margin-top:24px;font-size:10px;color:#94A3B8;text-align:right}
      @media print{body{padding:16px}}
    </style></head><body>
    <h1>${lead.name}</h1>
    <span class="status">${lead.status}</span>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:16px">
      ${lead.email ? `<div class="field"><div class="field-label">Email</div><div class="field-value">${lead.email}</div></div>` : ''}
      ${lead.phone ? `<div class="field"><div class="field-label">Phone</div><div class="field-value">${lead.phone}</div></div>` : ''}
      ${lead.company ? `<div class="field"><div class="field-label">Company</div><div class="field-value">${lead.company}</div></div>` : ''}
      ${lead.category ? `<div class="field"><div class="field-label">Category</div><div class="field-value">${lead.category}</div></div>` : ''}
    </div>
    ${(lead.address || lead.city || lead.state) ? `<div class="field" style="margin-top:8px"><div class="field-label">Address</div><div class="field-value">${[lead.address, lead.city, lead.state ? (lead.state + ' ' + (lead.zip_code || '')).trim() : lead.zip_code].filter(Boolean).join(', ')}</div></div>` : ''}
    ${lead.tags?.length ? `<div class="field" style="margin-top:8px"><div class="field-label">Tags</div>${lead.tags.map(t => `<span class="tag">${t}</span>`).join('')}</div>` : ''}
    ${lead.shared_with_names?.length ? `<div class="field"><div class="field-label">Shared With</div><div class="field-value">${lead.shared_with_names.join(', ')}</div></div>` : ''}
    <div class="field"><div class="field-label">Created By</div><div class="field-value">${lead.created_by_name} &middot; ${new Date(lead.created_at).toLocaleDateString()}</div></div>
    ${notes.length ? `<h2>Notes (${notes.length})</h2>${notes.map(n => `<div class="note"><div class="note-meta">${n.author} &middot; ${new Date(n.created_at).toLocaleString()}</div><div class="note-text">${n.content}</div></div>`).join('')}` : ''}
    ${activities.length ? `<h2>Activity Log</h2>${activities.map(a => `<div class="activity">${a.details} <span class="activity-time">&middot; ${a.user_name} &middot; ${new Date(a.timestamp).toLocaleString()}</span></div>`).join('')}` : ''}
    <p class="footer">Printed from Real Leads CRM</p></body></html>`;
    safePrint(html);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;
  if (!lead) return null;

  const isViewer = user?.role === 'viewer';

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8" data-testid="lead-detail-page">
      <Link to="/leads" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-leads">
        <ArrowLeft className="w-4 h-4" /> Back to Leads
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <LeadInfoCard lead={lead} isViewer={isViewer} onEdit={openEditLead} onShare={openShare} onPrint={handlePrintLead} onEmail={() => setShowEmailCompose(true)} onSnap={handleSnap} onRefresh={fetchLead} />
          {!isViewer && (
            <Link to={`/video/guest-rooms?lead_id=${id}`} className="inline-flex items-center gap-1.5 text-xs text-[#0047AB] border border-[#0047AB]/30 hover:bg-[#EFF6FF] rounded-sm px-3 py-1.5" data-testid="start-guest-video">
              <Video className="w-3.5 h-3.5" /> Start guest video call
            </Link>
          )}
          <LeadScoreCard score={lead.score} />
          <LeadNotesSection notes={lead.notes} isViewer={isViewer} note={note} setNote={setNote} onAddNote={handleAddNote} submitting={submitting} lead={lead} />
          <EmailHistory leadId={id} />
          <LeadSmsPanel lead={lead} isViewer={isViewer} />
          <LeadDocumentsSection
            documents={documents} isViewer={isViewer} uploading={uploading} dragOver={dragOver}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); const files = e.dataTransfer?.files; if (files?.length > 0) handleUploadDocument(files[0]); }}
            onFileSelect={(e) => { const file = e.target.files?.[0]; if (file) handleUploadDocument(file); e.target.value = ''; }}
            onDownload={handleDownloadDocument} onDelete={handleDeleteDocument}
          />
        </div>
        <div className="space-y-6">
          <NearbyExperts category={lead.category} leadName={lead.name} leadId={id} />
          <LeadActivityLog activities={lead.activities} />
        </div>
      </div>

      <LeadDetailShareDialog
        open={showShare} onClose={(v) => { setShowShare(v); if (!v) setShareNote(''); }} leadName={lead.name}
        members={members} groups={groups}
        selectedMembers={selectedMembers} setSelectedMembers={setSelectedMembers}
        selectedGroups={selectedGroups} setSelectedGroups={setSelectedGroups}
        shareTab={shareTab} setShareTab={setShareTab} onShare={handleShare}
        shareNote={shareNote} setShareNote={setShareNote}
      />

      <LeadEditDialog
        open={showEdit} onClose={setShowEdit}
        editForm={editForm} setEditForm={setEditForm}
        onSave={handleEditLead}
      />

      <EmailComposeDialog
        open={showEmailCompose}
        onClose={() => setShowEmailCompose(false)}
        lead={lead}
      />
    </div>
  );
}
